import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * useState, but the value survives a reload.
 *
 * Follows the same defensive contract the rest of the app uses for
 * localStorage (see context/ThemeContext.jsx): every access is wrapped, and a
 * stored value that fails `validate` is discarded in favour of `initialValue`.
 * That matters because these keys outlive deploys — a column name that no
 * longer exists, or a hand-edited entry, must never be able to break the view.
 *
 * Storage is unavailable in private-mode Safari and when the user blocks
 * site data, so a throwing read or write degrades to plain in-memory state
 * rather than taking the page down.
 *
 * @param {string}   key           storage key (convention: `qdex-…`)
 * @param {*}        initialValue  used when nothing valid is stored
 * @param {Function} [validate]    (parsed) => boolean — reject unusable values
 * @returns {[*, Function]} the same tuple useState returns
 */
export default function usePersistedState(key, initialValue, validate) {
    const validateRef = useRef(validate);
    validateRef.current = validate;

    const [value, setValue] = useState(() => {
        try {
            const raw = localStorage.getItem(key);
            if (raw === null) return initialValue;
            const parsed = JSON.parse(raw);
            if (validateRef.current && !validateRef.current(parsed)) return initialValue;
            return parsed;
        } catch {
            return initialValue;
        }
    });

    useEffect(() => {
        try {
            localStorage.setItem(key, JSON.stringify(value));
        } catch {
            // Quota exceeded or storage blocked — the preference simply won't
            // persist. Never surface this; it isn't actionable for the user.
        }
    }, [key, value]);

    // Stable identity so callers can safely list it in a dependency array.
    const set = useCallback((next) => setValue(next), []);

    return [value, set];
}
